import { useInfiniteQuery } from "@tanstack/react-query";
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MarketingExportButton } from "@/components/photo-studio/marketing/marketing-export-button";
import { AttachmentsGrid } from "@/components/scribe/attachments-grid";
import { GallerySkeleton } from "@/components/scribe/gallery-skeleton";
import { DateRange } from "@/components/ui/date-picker";
import { useUser } from "@/contexts/user-provider";
import { pickedDay } from "@/lib/clinic-clock";
import { PERMISSIONS, hasPermission } from "@/lib/permissions";
import { AttachmentDayPage } from "@/types/notes";
import { noteQueryOptions } from "@/api/provider/scribe/note-query";
export const GallerySection = ({
  patientId,
  size,
  mode = "patient",
  showUpload = true,
  dateRange,
  tagIds: initialTagIds,
  onTagIdsChange,
  showPatientLink = false,
  optedInToSocialMedia,
  toolbarActions
}: {
  patientId?: string;
  size?: "sm" | "lg";
  mode?: "patient" | "global";
  showUpload?: boolean;
  dateRange?: DateRange;
  tagIds?: string[];
  // Told when someone changes the tag filter in the grid
  onTagIdsChange?: (tagIds: string[]) => void;
  showPatientLink?: boolean;
  optedInToSocialMedia?: boolean;
  // Extra buttons in the gallery toolbar, just before Capture
  toolbarActions?: ReactNode;
}) => {
  const {
    currentUser
  } = useUser();
  const canViewGallery = hasPermission(currentUser, PERMISSIONS.CLINICAL_GALLERY_READ);
  const [tagIds, setTagIds] = useState<string[]>(initialTagIds || []);

  // Sync with prop changes (only if prop is explicitly provided)
  useEffect(() => {
    if (initialTagIds !== undefined) {
      setTagIds(initialTagIds);
    }
  }, [initialTagIds]);

  // Calculate initial date for query - use end date (latest) from range if provided
  const initialDate = useMemo(() => {
    if (dateRange?.to) {
      return pickedDay(dateRange.to);
    }
    return undefined;
  }, [dateRange?.to]);
  const queryOptions = noteQueryOptions.attachmentsInfinite(mode === "patient" || mode === "global" && patientId ? patientId : undefined, tagIds, optedInToSocialMedia);
  const {
    data,
    isLoading: loadingAttachments,
    error: attachmentsError,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage
  } = useInfiniteQuery({
    ...queryOptions,
    queryKey: [...queryOptions.queryKey, {
      dateRangeFrom: dateRange?.from?.toISOString() ?? null,
      dateRangeTo: dateRange?.to?.toISOString() ?? null
    }],
    enabled: canViewGallery && (mode === "patient" ? !!patientId : true),
    initialPageParam: initialDate,
    getNextPageParam: (lastPage: AttachmentDayPage | null) => {
      if (!lastPage?.nextDate) return undefined;

      // If no date range, use default behavior
      if (!dateRange?.from) return lastPage.nextDate;

      // Page dates are `YYYY-MM-DD` day keys, so they compare as strings.
      // Only return nextDate if it's >= startDate (going backwards)
      return lastPage.nextDate >= pickedDay(dateRange.from) ? lastPage.nextDate : undefined;
    }
  });
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const setObserver = useCallback((node: HTMLDivElement | null) => {
    if (isFetchingNextPage) return;
    if (observerRef.current) observerRef.current.disconnect();
    observerRef.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasNextPage) {
        fetchNextPage();
      }
    });
    if (node) observerRef.current.observe(node);
    sentinelRef.current = node;
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  // Filter pages based on date range (safety filter in case API returns pages outside range)
  const filteredPages = useMemo(() => {
    const allPages: AttachmentDayPage[] = data?.pages?.filter(Boolean) as AttachmentDayPage[] ?? [];
    if (!dateRange) {
      return allPages;
    }
    return allPages.filter(page => {
      // Page dates are `YYYY-MM-DD` day keys, so they compare as strings.
      if (dateRange.from) {
        // Include dates >= startDate (inclusive)
        if (page.date < pickedDay(dateRange.from)) return false;
      }
      if (dateRange.to) {
        // Include dates <= endDate (inclusive)
        if (page.date > pickedDay(dateRange.to)) return false;
      }
      return true;
    });
  }, [data?.pages, dateRange]);
  const pages = filteredPages;

  // Flatten pages into attachments array with dates
  const attachments = useMemo(() => {
    return pages?.flatMap(page_0 => page_0.data.map(attachment => ({
      id: attachment.id,
      attachmentId: attachment.id,
      url: attachment.signedUrl || "",
      signedUrl: attachment.signedUrl,
      thumbnailSignedUrl: attachment.thumbnailSignedUrl,
      gcsPath: attachment.gcsPath,
      annotations: attachment.annotations || [],
      name: attachment.name,
      date: page_0.date,
      uploadedAt: attachment.uploadedAt,
      noteId: attachment.noteId,
      // Include patient info if available from API
      patient: attachment.patient,
      note: attachment.note,
      tags: attachment.tags,
      visibleToPatient: attachment.visibleToPatient,
      patientCaption: attachment.patientCaption
    }))) ?? [];
  }, [pages]);
  if (!canViewGallery) {
    return <div className="flex flex-col items-center justify-center p-4 text-center">
        <p className="text-caption text-muted-foreground">
          You don&apos;t have permission to view the gallery
        </p>
      </div>;
  }
  if (loadingAttachments) {
    return <GallerySkeleton size={size} />;
  }
  if (attachmentsError) {
    return <div className="flex flex-col items-center justify-center p-8 text-center">
        <div className="text-body text-red-600">
          {attachmentsError.message || "Error loading attachments"}
        </div>
      </div>;
  }
  return <>
      <AttachmentsGrid attachments={attachments} isLoading={isFetchingNextPage} hasNextPage={hasNextPage} isFetchingNextPage={isFetchingNextPage} fetchNextPage={fetchNextPage} loadMoreRef={setObserver} size={size} groupByDate={true} showUpload={showUpload} uploadDisabled={!patientId} patientId={patientId} tagIds={tagIds} onTagIdsChange={newTagIds => {
      setTagIds(newTagIds);
      onTagIdsChange?.(newTagIds);
    }} showPatientLink={showPatientLink} enablePortalSharing toolbarActions={<>
            {patientId && <MarketingExportButton patientId={patientId} compact />}
            {toolbarActions}
          </>} />
    </>;
};